import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Link, useLocation } from "react-router-dom";
import { HashLink } from 'react-router-hash-link';
import ContactButton from "./ContactButton";
import { iconButtonBase, secondaryButtonBase } from "../styles/buttonStyles";
import { typography } from "../styles/designSystem";

const Nav = styled.nav`
    position: relative;
    width: 100%;
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
    padding: 0.75rem 2rem;
    min-height: 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
    border-top: 1px solid ${({ theme }) => theme.borderLight};
    border-bottom: 1px solid ${({ theme }) => theme.borderMedium};
    box-shadow: 0 0 20px ${({ theme }) => theme.glowSoft};
    box-sizing: border-box;

    @media (max-width: 1280px) {
        padding: 1rem 1rem;
    }

    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

const LeftSide = styled.div`
    display: flex;
    align-items: center;
    gap: 1.5rem;

    @media (max-width: 1280px) {
        gap: 0.5rem;
    }
`;

const RightSide = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;

    @media (max-width: 1280px) {
        gap: 0.5rem;
    }
`;

const NavList = styled.ul`
    display: flex;
    gap: 2rem;
    list-style: none;
    margin: 0;
    padding: 0;

    @media (max-width: 768px) {
        position: fixed;
        top: 0;
        right: ${({ open }) => (open ? "0" : "-100%")};
        height: 100vh;
        width: 260px;
        background: ${({ theme }) => theme.background};
        border-left: 1px solid ${({ theme }) => theme.borderMedium};
        box-shadow: -8px 0 32px rgba(0, 0, 0, 0.6);
        flex-direction: column;
        align-items: flex-start;
        padding: 4rem 1.5rem;
        transition: right 0.3s ease;
        z-index: 1002;
    }

    @media (min-width: 769px) and (max-width: 1280px) {
        gap: 0.4rem;
    }

    @media (min-width: 769px) {
        display: flex !important;
        position: static;
        flex-direction: row;
        height: auto;
        width: auto;
        background: transparent;
        padding: 0;
    }
`;

// Nav entries read as terminal menu options: tracked-out mono, and a `>` marker
// that appears in reserved space on hover so nothing shifts sideways.
const navEntry = `
    font-family: ${typography.fontFamily.display};
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    padding: 0.45rem 0.75rem 0.45rem 1.1rem;
    display: inline-block;
    position: relative;
`;

const NavItem = styled.li`
    a {
        ${navEntry}
        text-decoration: none;
        color: ${({ theme }) => theme.text};

        &::before {
            content: ">";
            position: absolute;
            left: 0.25rem;
            opacity: 0;
            color: ${({ theme }) => theme.primary};
            transition: opacity 0.12s ease-out;
        }

        &:hover,
        &[aria-current="page"] {
            color: ${({ theme }) => theme.primary};
            text-shadow: 0 0 8px ${({ theme }) => theme.glow};

            &::before {
                opacity: 1;
            }
        }

        @media (min-width: 769px) and (max-width: 1280px) {
            font-size: 0.78rem;
            padding: 0.45rem 0.3rem 0.45rem 0.9rem;
        }
    }
`;



const MenuButton = styled.button`
    ${iconButtonBase}
    font-size: 1.5rem;
    color: ${({ theme }) => theme.text};

    @media (min-width: 769px) {
        display: none !important;
    }
`;

// Phosphor selector — the old light/dark switch. Both modes are dark terminals,
// so this picks the CRT's phosphor color rather than the page's luminance.
const PhosphorToggle = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.borderMedium};
    border-radius: 0;
    padding: 0.35rem 0.7rem;
    cursor: pointer;
    font-family: ${typography.fontFamily.display};
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wider};
    color: ${({ theme }) => theme.primary};
    white-space: nowrap;
    transition: box-shadow 0.14s ease-out, border-color 0.14s ease-out;

    &:hover {
        border-color: ${({ theme }) => theme.primary};
        box-shadow: 0 0 12px ${({ theme }) => theme.glowSoft};
    }

    /* The lit indicator lamp for the currently selected phosphor. */
    &::before {
        content: "";
        width: 8px;
        height: 8px;
        flex: none;
        background: ${({ theme }) => theme.primary};
        box-shadow: 0 0 8px ${({ theme }) => theme.glow};
    }

    @media (max-width: 1280px) {
        font-size: 0.62rem;
        padding: 0.3rem 0.5rem;
        letter-spacing: 0.1em;
    }

    @media (max-width: 480px) {
        /* Keep the lamp, drop the label — the bar is tight on phones. */
        gap: 0;

        span {
            display: none;
        }
    }
`;

const DropdownWrapper = styled.li`
    position: relative;
    width: 100%;
    @media (min-width: 769px) {
        width: auto;
    }
`;

const DropdownToggle = styled.button`
    ${secondaryButtonBase}
    color: ${({ theme }) => theme.text};
    font-size: 0.9rem;
    gap: 0.35rem;
    padding: 0.45rem 0.75rem;

    &:hover,
    &[aria-expanded="true"] {
        color: ${({ theme }) => theme.primary};
        text-shadow: 0 0 8px ${({ theme }) => theme.glow};
    }

    @media (min-width: 769px) and (max-width: 1280px) {
        font-size: 0.78rem;
        padding: 0.45rem 0.3rem;
    }
`;

const DropdownMenu = styled.ul`
    display: ${({ open }) => (open ? "block" : "none")};
    position: absolute;
    top: 2.4rem;
    left: 0;
    background: ${({ theme }) => theme.background};
    border: 1px solid ${({ theme }) => theme.borderMedium};
    box-shadow: 0 0 18px ${({ theme }) => theme.glowSoft}, inset 0 0 24px rgba(0, 0, 0, 0.5);
    list-style: none;
    padding: 0.4rem 0;
    min-width: 210px;
    z-index: 100;

    @media (max-width: 768px) {
        position: static;
        border: none;
        border-left: 1px solid ${({ theme }) => theme.borderLight};
        margin-left: 0.5rem;
        box-shadow: none;
        background: none;
        padding-left: 0;
    }
`;

const DropdownItem = styled.li`
    a {
        display: block;
        padding: 0.4rem 1rem 0.4rem 1.6rem;
        position: relative;
        text-decoration: none;
        color: ${({ theme }) => theme.text};
        font-family: ${typography.fontFamily.display};
        font-size: 0.85rem;
        text-transform: uppercase;
        letter-spacing: ${typography.letterSpacing.wide};

        &::before {
            content: "\\2022";
            position: absolute;
            left: 0.7rem;
            opacity: 0.4;
            color: ${({ theme }) => theme.primary};
        }

        &:hover {
            color: ${({ theme }) => theme.background};
            background: ${({ theme }) => theme.primary};
            text-shadow: none;

            &::before {
                content: "\\25B8";
                opacity: 1;
                color: ${({ theme }) => theme.background};
            }
        }
    }
`;


const Header = ({ darkMode, toggleDarkMode }) => {
    const [open, setOpen] = useState(false);
    // One id rather than a boolean per menu: three independent flags let every
    // dropdown be open at once, which stacked overlapping panels on top of the nav.
    const [openMenu, setOpenMenu] = useState(null);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
        setOpenMenu(null);
    }, [location]);

    const toggleMenu = () => setOpen((prev) => !prev);
    // Opening one menu closes whichever was already open; clicking the open one closes it.
    const toggleDropdown = (id) => setOpenMenu((prev) => (prev === id ? null : id));

    return (
        <Nav>
            <LeftSide>
                <MenuButton
                    onClick={toggleMenu}
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                >
                    <FontAwesomeIcon icon={open ? faXmark : faBars} />
                </MenuButton>
                <NavList open={open}>
                    <NavItem>
                        <HashLink to="/" onClick={() => window.scrollTo(0, 0)}>Home</HashLink>
                    </NavItem>
                    <DropdownWrapper>
                        <DropdownToggle
                            onClick={() => toggleDropdown("approach")}
                            aria-haspopup="true"
                            aria-expanded={openMenu === "approach"}
                            aria-label="How I Work menu"
                        >
                            How I Work ▾
                        </DropdownToggle>
                        <DropdownMenu open={openMenu === "approach"} role="menu">
                            <DropdownItem><Link to="/how-i-build">How I Build</Link></DropdownItem>
                            <DropdownItem><Link to="/leading-engineers">Leading Engineers</Link></DropdownItem>
                            <DropdownItem><Link to="/system-design">System Design</Link></DropdownItem>
                        </DropdownMenu>
                    </DropdownWrapper>
                    <DropdownWrapper>
                        <DropdownToggle
                            onClick={() => toggleDropdown("portfolio")}
                            aria-haspopup="true"
                            aria-expanded={openMenu === "portfolio"}
                            aria-label="Portfolio menu"
                        >
                            Portfolio ▾
                        </DropdownToggle>
                        <DropdownMenu open={openMenu === "portfolio"} role="menu">
                            <DropdownItem><Link to="/portfolio/nebo">Nebo</Link></DropdownItem>
                            <DropdownItem><Link to="/portfolio/cartsense">CartSense</Link></DropdownItem>
                            <DropdownItem><Link to="/portfolio/figma-to-code">Figma to Code</Link></DropdownItem>
                        </DropdownMenu>
                    </DropdownWrapper>
                    <DropdownWrapper>
                        <DropdownToggle
                            onClick={() => toggleDropdown("experience")}
                            aria-haspopup="true"
                            aria-expanded={openMenu === "experience"}
                            aria-label="Experience menu"
                        >
                            Experience ▾
                        </DropdownToggle>
                        <DropdownMenu open={openMenu === "experience"} role="menu">
                            <DropdownItem><Link to="/work/att">AT&amp;T</Link></DropdownItem>
                            <DropdownItem><Link to="/work/NsaBrands">NSA Brands</Link></DropdownItem>
                            <DropdownItem><Link to="/work/spectrum">Spectrum</Link></DropdownItem>
                            <DropdownItem><Link to="/work/tilt">tilt by Smarterhome</Link></DropdownItem>
                            <DropdownItem><Link to="/work/Verizon">Verizon</Link></DropdownItem>
                            <DropdownItem><Link to="/work/Caterpillar">Caterpillar</Link></DropdownItem>
                        </DropdownMenu>
                    </DropdownWrapper>
                    <NavItem>
                        <Link to="/components">Registry</Link>
                    </NavItem>
                    <NavItem>
                        <Link to="/contact">Contact</Link>
                    </NavItem>
                </NavList>
            </LeftSide>
            <RightSide>
                <ContactButton size="small" />
                <PhosphorToggle
                    onClick={toggleDarkMode}
                    aria-label={
                        darkMode
                            ? "Switch terminal phosphor to Vault-Tec Amber"
                            : "Switch terminal phosphor to Pip-Boy Green"
                    }
                >
                    <span>{darkMode ? "Pip-Boy Green" : "Vault-Tec Amber"}</span>
                </PhosphorToggle>
            </RightSide>
        </Nav>
    );
};

export default Header;
